import React, { useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import { ChevronLeft, History, RotateCcw, X } from 'lucide-react';
import { WikiPageHistory as WikiPageHistoryType } from '../../types/wiki';
import { Button, IconButton, Pill } from '../ui';
import { formatDateTime, looksLikeHtml, readableAuthor, timeAgo } from './wikiFormat';

interface WikiPageHistoryProps {
  history: WikiPageHistoryType[];
  currentVersion: number;
  onRestore?: (version: number) => Promise<void>;
  onClose: () => void;
  isLoading?: boolean;
  className?: string;
}

export const WikiPageHistory: React.FC<WikiPageHistoryProps> = ({
  history,
  currentVersion,
  onRestore,
  onClose,
  isLoading = false,
  className = '',
}) => {
  const [selectedVersion, setSelectedVersion] = useState<WikiPageHistoryType | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);

  const handleRestore = useCallback(async (version: number) => {
    if (!onRestore) return;

    setIsRestoring(true);
    try {
      await onRestore(version);
    } catch (error) {
      console.error('Error restoring version:', error);
    } finally {
      setIsRestoring(false);
    }
  }, [onRestore]);

  const handleViewVersion = useCallback((historyItem: WikiPageHistoryType) => {
    setSelectedVersion(historyItem);
  }, []);

  const handleClosePreview = useCallback(() => {
    setSelectedVersion(null);
  }, []);

  const previewHtml = useMemo(() => {
    const content = selectedVersion?.content || '';
    return looksLikeHtml(content) ? DOMPurify.sanitize(content) : null;
  }, [selectedVersion]);

  const closeButton = (
    <IconButton label="Close" size="sm" onClick={onClose} className="-mr-1 bg-neutral-100 text-neutral-600">
      <X className="h-4 w-4" strokeWidth={1.75} />
    </IconButton>
  );

  if (selectedVersion) {
    const author = readableAuthor(selectedVersion.createdBy);
    return (
      <div className={`font-ui text-ink ${className}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <button
              type="button"
              onClick={handleClosePreview}
              className="-ml-1 mb-1 inline-flex h-7 items-center gap-0.5 rounded-full pl-0.5 pr-2 text-[13px] text-neutral-500 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
              Back to history
            </button>
            <h2 className="text-[18px] font-normal leading-snug tracking-[-0.02em] text-ink">
              Version {selectedVersion.version} - {selectedVersion.title}
            </h2>
            <p className="mt-0.5 text-[12px] text-neutral-400">
              {[author, formatDateTime(selectedVersion.createdAt)].filter(Boolean).join(' · ')}
            </p>
          </div>
          {closeButton}
        </div>

        {selectedVersion.changeDescription && (
          <p className="mt-4 rounded-xl bg-neutral-100 px-3.5 py-2.5 text-[13px] text-neutral-600">
            {selectedVersion.changeDescription}
          </p>
        )}

        <div className="mt-5 border-t border-neutral-200/70 pt-5">
          {previewHtml !== null ? (
            <div className="wiki-article" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          ) : (
            <div className="wiki-article">
              <ReactMarkdown>{selectedVersion.content}</ReactMarkdown>
            </div>
          )}
        </div>

        {onRestore && selectedVersion.version !== currentVersion && (
          <div className="mt-6 flex justify-end">
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleRestore(selectedVersion.version)}
              disabled={isRestoring || isLoading}
            >
              <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
              {isRestoring ? 'Restoring…' : 'Restore this version'}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`font-ui text-ink ${className}`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-[18px] font-normal tracking-[-0.02em] text-ink">Version history</h2>
        {closeButton}
      </div>

      {history.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 text-neutral-400">
            <History className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </span>
          <p className="text-[13px] text-neutral-500">No version history available.</p>
        </div>
      ) : (
        <ol aria-label="Versions">
          {history.map((historyItem, index) => {
            const isCurrent = historyItem.version === currentVersion;
            const author = readableAuthor(historyItem.createdBy);
            const last = index === history.length - 1;
            return (
              <li key={historyItem.id} className="relative flex gap-3">
                {/* Timeline rail */}
                <span className="relative flex w-3 flex-shrink-0 justify-center" aria-hidden="true">
                  <span
                    className={`relative z-10 mt-[7px] h-2 w-2 rounded-full ring-4 ring-white ${
                      isCurrent ? 'bg-primary-600' : 'bg-neutral-300'
                    }`}
                  />
                  {!last && <span className="absolute bottom-0 top-[15px] w-px bg-neutral-200" />}
                </span>

                <div className={`flex min-w-0 flex-1 items-start justify-between gap-3 ${last ? '' : 'pb-5'}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] text-ink">Version {historyItem.version}</span>
                      {isCurrent && <Pill tone="neutral">Current</Pill>}
                    </div>
                    <p className="mt-0.5 truncate text-[12px] text-neutral-400" title={formatDateTime(historyItem.createdAt)}>
                      {[author, timeAgo(historyItem.createdAt)].filter(Boolean).join(' · ')}
                    </p>
                    {historyItem.changeDescription && (
                      <p className="mt-1 break-words text-[13px] text-neutral-500">{historyItem.changeDescription}</p>
                    )}
                  </div>

                  <div className="flex flex-shrink-0 items-center gap-1">
                    <Button size="xs" variant="ghost" onClick={() => handleViewVersion(historyItem)}>
                      View
                    </Button>
                    {onRestore && !isCurrent && (
                      <Button
                        size="xs"
                        variant="secondary"
                        onClick={() => handleRestore(historyItem.version)}
                        disabled={isRestoring || isLoading}
                      >
                        <RotateCcw className="h-3 w-3" strokeWidth={1.75} />
                        Restore
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
